// Panel derecho de «Cobros»: encabezado del crédito, cupón vigente, cobro en tienda, equipo e historial.
import { AlertTriangle, CalendarDays, Lightbulb, Lock, Smartphone, Wallet } from 'lucide-react';
import { LoanCreditStatusEnum } from '@fiado/type-kit/bin/loanCredit/enums/LoanCreditStatusEnum';
import { LoanDeviceMdmStatusEnum } from '@fiado/type-kit/bin/loanCredit/enums/LoanDeviceMdmStatusEnum';
import { ErrorState, Skeleton } from '@/kit';
import { cn } from '@/lib/cn';
import { NoSourceBlock } from '@/shared/patterns/NoSource';
import { mxDay } from '@/lib/dateHelpers';
import {
    paymentChargeKindLabelOf,
    paymentOriginFactsOf,
} from '@/domains/credit/payments/paymentOrigin';
import type { SaleDeviceState } from '../../mysales/api/useSaleDevice';
import type { PaymentsRead } from '../api/useCollections';
import type { SellerPaymentResult } from '@/api/services/loan-credit';
import { ChargeFlow } from './ChargeFlow';
import { PaymentReceipt } from './PaymentReceipt';
import { paymentCouponLabelOf } from '@/domains/credit/installments/payments';
import {
    CLIENT_LEVEL_LABEL,
    couponViewOf,
    couponWhen,
    walkInCouponOf,
    type CollectionCredit,
    type CouponView,
    type CreditFacts,
} from '../lib/collectionsModel';
import {
    CREDIT_ERROR_NOTE,
    CREDIT_FORBIDDEN_NOTE,
    dueDateLabel,
    instantLabel,
    money,
    NO_PAYMENTS_TEXT,
    NO_SELECTION_TEXT,
    NO_SELECTION_TEXT_READ_ONLY,
    NO_SELECTION_TITLE,
    PAYMENTS_ERROR_NOTE,
    PAYMENTS_FORBIDDEN_NOTE,
    SETTLED_TEXT,
} from '../lib/collectionsFormat';

export interface CollectionDetailPanelProps {
    row: CollectionCredit | null;
    facts: CreditFacts | null;
    payments: PaymentsRead;
    device: SaleDeviceState;
    /** Con `loan.credit.collection.write` se muestra el cobro en tienda. */
    canCollect: boolean;
    /** Comprobante del último cobro: se queda aunque el crédito ya no tenga cupón. */
    receipt: SellerPaymentResult | null;
    onCharged: (result: SellerPaymentResult) => void;
    onReceiptDone: () => void;
    onRetry: () => void;
}

export function CollectionDetailPanel({
    row,
    facts,
    payments,
    device,
    canCollect,
    receipt,
    onCharged,
    onReceiptDone,
    onRetry,
}: CollectionDetailPanelProps) {
    if (!row) {
        return (
            <div className="flex flex-1 items-center justify-center p-10 text-center text-muted-foreground">
                <div>
                    <Wallet aria-hidden className="mx-auto mb-2.5 size-11 opacity-50" />
                    <div className="max-w-[340px] text-[13px] leading-normal">
                        <strong>{NO_SELECTION_TITLE}</strong>
                        {canCollect ? NO_SELECTION_TEXT : NO_SELECTION_TEXT_READ_ONLY}
                    </div>
                </div>
            </div>
        );
    }
    return (
        <>
            <DetailHead row={row} facts={facts} />
            <div className="flex flex-1 flex-col gap-3.5 overflow-y-auto px-[18px] py-4">
                <DetailBody
                    row={row}
                    facts={facts}
                    payments={payments}
                    device={device}
                    canCollect={canCollect}
                    receipt={receipt}
                    onCharged={onCharged}
                    onReceiptDone={onReceiptDone}
                    onRetry={onRetry}
                />
            </div>
        </>
    );
}

const LEVEL_CHIP: Record<string, string> = {
    GOLD: 'bg-tier-gold-soft text-tier-gold',
    SILVER: 'bg-tier-silver-soft text-tier-silver',
    BRONZE: 'bg-tier-bronze-soft text-tier-bronze',
};

function DetailHead({ row, facts }: { row: CollectionCredit; facts: CreditFacts | null }) {
    const level = facts?.clientLevel ?? null;
    const pct =
        facts && facts.termWeeks > 0
            ? Math.round((facts.paidInstallments / facts.termWeeks) * 100)
            : 0;
    return (
        <div className="grid grid-cols-[1fr_auto] items-start gap-3 border-b border-border bg-muted/30 px-[18px] py-3.5">
            <div>
                <div className="text-[15px] font-extrabold leading-tight">{row.customerName}</div>
                <div className="mt-0.5 text-[11px] text-muted-foreground">
                    {/* eslint-disable-next-line no-restricted-syntax -- el id del crédito es su folio: el mock lo pinta y el walk-in lo busca */}
                    <span className="font-mono font-semibold text-foreground">{row.creditId}</span>
                    {row.product ? ` · ${row.product}` : ''}
                    {level && CLIENT_LEVEL_LABEL[level] ? (
                        <span
                            className={cn(
                                'ml-1 rounded-sm px-1.5 py-px text-[9px] font-bold uppercase tracking-wide',
                                LEVEL_CHIP[level],
                            )}
                        >
                            {CLIENT_LEVEL_LABEL[level]}
                        </span>
                    ) : null}
                </div>
            </div>
            {facts ? (
                <div className="text-right text-[11px] text-muted-foreground">
                    <div className="text-[15px] font-extrabold tabular-nums text-foreground">
                        {facts.paidInstallments} / {facts.termWeeks}
                    </div>
                    <div>cupones pagados</div>
                    <div className="ml-auto mt-1 h-[5px] w-[140px] overflow-hidden rounded-sm bg-muted">
                        <div className="h-full bg-success" style={{ width: `${String(pct)}%` }} />
                    </div>
                    <div className="mt-[3px]">
                        {typeof facts.totalDebtCents === 'number' ? (
                            <>
                                Deuda total:{' '}
                                <strong className="tabular-nums text-foreground">
                                    {money(facts.totalDebtCents)}
                                </strong>
                            </>
                        ) : (
                            <>
                                Saldo:{' '}
                                <strong className="tabular-nums text-foreground">
                                    {facts.outstandingBalanceCents === null
                                        ? '—'
                                        : money(facts.outstandingBalanceCents)}
                                </strong>
                            </>
                        )}
                    </div>
                </div>
            ) : null}
        </div>
    );
}

function DetailBody({
    row,
    facts,
    payments,
    device,
    canCollect,
    receipt,
    onCharged,
    onReceiptDone,
    onRetry,
}: Omit<CollectionDetailPanelProps, 'row'> & { row: CollectionCredit }) {
    if (receipt) {
        return (
            <>
                <PaymentReceipt result={receipt} onAnother={onReceiptDone} />
                <PaymentsHistory payments={payments} />
            </>
        );
    }
    if (row.read.state === 'walk-in') {
        const coupon = walkInCouponOf(row.read.lookup, mxDay(new Date().toISOString()));
        return (
            <>
                {coupon ? <CouponCard coupon={coupon} /> : null}
                {canCollect ? (
                    <ChargeFlow key={row.creditId} creditId={row.creditId} onCharged={onCharged} />
                ) : null}
                <PaymentsHistory payments={payments} />
            </>
        );
    }
    if (row.read.state === 'loading') return <Skeleton lines={6} />;
    if (row.read.state === 'forbidden')
        return <NoSourceBlock>{CREDIT_FORBIDDEN_NOTE}</NoSourceBlock>;
    if (row.read.state === 'error' || !facts) {
        return <ErrorState description={CREDIT_ERROR_NOTE} onRetry={onRetry} />;
    }
    const coupon = couponViewOf(facts);
    return (
        <>
            {coupon ? (
                <CouponCard coupon={coupon} />
            ) : facts.status === LoanCreditStatusEnum.SETTLED ? (
                <div className="rounded-[10px] border border-success/30 bg-success-soft px-[18px] py-4 text-center font-bold text-success">
                    {SETTLED_TEXT}
                </div>
            ) : null}
            {canCollect && facts.coupon ? (
                <ChargeFlow key={row.creditId} creditId={row.creditId} onCharged={onCharged} />
            ) : null}
            {facts.coupon && facts.isHighEnd ? (
                <div className="rounded-md bg-warning-soft px-3 py-2 text-[11px] font-semibold text-warning">
                    <Lightbulb className="mr-1 inline size-3.5 align-[-3px]" aria-hidden />
                    Gama alta · este pago te genera <strong>1% de renta cartera</strong> (RC-13, se
                    paga fin de mes)
                </div>
            ) : null}
            <DeviceCard row={row} device={device} />
            <PaymentsHistory payments={payments} />
        </>
    );
}

function CouponCard({ coupon }: { coupon: CouponView }) {
    const inArrears = coupon.dueIn !== null && coupon.dueIn < 0;
    const when = coupon.dueIn === null ? null : couponWhen(coupon.dueIn);
    return (
        <div
            className={cn(
                'flex items-center gap-2.5 rounded-lg border px-3.5 py-2.5 text-xs',
                inArrears ? 'border-danger/35 bg-danger-soft/40' : 'border-border bg-muted/30',
            )}
        >
            {inArrears ? (
                <AlertTriangle aria-hidden className="size-4 flex-none text-danger" />
            ) : (
                <CalendarDays aria-hidden className="size-4 flex-none text-muted-foreground" />
            )}
            <div className="flex-1">
                <div className="text-[9.5px] font-bold uppercase tracking-wide text-muted-foreground">
                    Cupón vigente
                </div>
                <div className="mt-px text-xs font-bold">
                    <span className="font-mono">#{coupon.installmentNumber}</span>
                    {coupon.termWeeks !== null ? ` de ${String(coupon.termWeeks)}` : ''} ·{' '}
                    {dueDateLabel(coupon.dueDate)}
                    {when ? (
                        <span
                            className={cn(
                                'ml-1.5',
                                when.tone === 'arrears' && 'font-bold text-danger',
                                when.tone === 'today' && 'font-semibold text-success',
                                when.tone === 'future' && 'font-medium text-muted-foreground',
                            )}
                        >
                            ({when.text})
                        </span>
                    ) : null}
                </div>
            </div>
            <div
                className={cn(
                    'text-[15px] font-bold tabular-nums',
                    inArrears ? 'text-danger' : 'text-foreground',
                )}
            >
                {money(coupon.amountCents)}
            </div>
        </div>
    );
}

/** El expediente trae el estado como texto crudo del silo. */
const LOCKED_STATUS: string = LoanDeviceMdmStatusEnum.LOCKED;

function DeviceCard({ row, device }: { row: CollectionCredit; device: SaleDeviceState }) {
    if (device.status !== 'ready' || !device.data.device) return null;
    const expedient = device.data.device;
    const blocked = expedient.mdmStatus === LOCKED_STATUS;
    const since = expedient.lockedAt ? ` desde ${instantLabel(expedient.lockedAt)}` : '';
    return (
        <div className="grid grid-cols-[auto_1fr_auto] items-center gap-3 rounded-lg bg-muted/30 px-3.5 py-2.5 text-[11.5px]">
            <div
                aria-hidden
                className={cn(
                    'flex size-8 items-center justify-center rounded-lg text-base text-white',
                    blocked ? 'bg-danger' : 'bg-success',
                )}
            >
                {blocked ? (
                    <Lock className="size-4" aria-hidden />
                ) : (
                    <Smartphone className="size-4" aria-hidden />
                )}
            </div>
            <div>
                <div className="text-[9.5px] font-bold uppercase tracking-wide text-muted-foreground">
                    Equipo · IMEI
                </div>
                <div className="font-mono text-[11px] font-bold">
                    {expedient.imei ?? row.imei ?? '—'}
                </div>
                <div className="mt-px text-[10.5px] text-muted-foreground">
                    {row.product}
                    {blocked ? ` · bloqueado por ${expedient.lockReason ?? 'mora'}${since}` : ''}
                </div>
            </div>
            <div
                className={cn(
                    'rounded-sm px-2 py-[3px] text-[9.5px] font-bold uppercase tracking-wide',
                    blocked ? 'bg-danger-soft text-danger' : 'bg-success-soft text-success',
                )}
            >
                {blocked ? 'Bloqueado' : 'Activo'}
            </div>
        </div>
    );
}

function PaymentsHistory({ payments }: { payments: PaymentsRead }) {
    return (
        <div>
            <div className="mb-1.5 text-[10.5px] font-bold uppercase tracking-[0.06em] text-muted-foreground">
                Últimos cobros
            </div>
            <div className="rounded-lg bg-muted/20 px-3 py-2.5">
                <PaymentsBody payments={payments} />
            </div>
        </div>
    );
}

function PaymentsBody({ payments }: { payments: PaymentsRead }) {
    if (payments.state === 'loading') return <Skeleton lines={3} />;
    if (payments.state === 'forbidden')
        return <NoSourceBlock>{PAYMENTS_FORBIDDEN_NOTE}</NoSourceBlock>;
    if (payments.state === 'error') return <NoSourceBlock>{PAYMENTS_ERROR_NOTE}</NoSourceBlock>;
    if (payments.items.length === 0) {
        return (
            <div className="p-3 text-center text-[11px] italic text-muted-foreground">
                {NO_PAYMENTS_TEXT}
            </div>
        );
    }
    return (
        <ul className="divide-y divide-dashed divide-border">
            {payments.items.map((payment) => {
                const origin = paymentOriginFactsOf(payment);
                const originText = [origin.where, origin.how, origin.who]
                    .filter(Boolean)
                    .join(' · ');
                const coupon = paymentCouponLabelOf(payment);
                const kind = paymentChargeKindLabelOf(payment.chargeKind);
                const headline = [coupon, instantLabel(payment.appliedAt)]
                    .concat(coupon === kind ? [] : [kind])
                    .join(' · ');
                return (
                    <li
                        key={payment.paymentId}
                        className="grid grid-cols-[auto_1fr_auto] items-center gap-x-2.5 py-[5px] text-[11.5px] tabular-nums"
                    >
                        <span aria-hidden className="size-2 rounded-full bg-success" />
                        <div className="min-w-0">
                            <div className="truncate" title={headline}>
                                <strong>{coupon}</strong> · {instantLabel(payment.appliedAt)}
                                {coupon === kind ? null : (
                                    <span className="ml-1.5 rounded-sm bg-muted px-1.5 py-px text-[9.5px] font-bold uppercase tracking-wide text-muted-foreground">
                                        {kind}
                                    </span>
                                )}
                            </div>
                            <div className="flex gap-1.5 text-[10.5px] text-muted-foreground">
                                <span className="truncate" title={originText}>
                                    {originText}
                                </span>
                                <span className="flex-none font-mono">{payment.receipt}</span>
                            </div>
                        </div>
                        <div className="font-bold text-success">{money(payment.appliedCents)}</div>
                    </li>
                );
            })}
        </ul>
    );
}
